import { onAuthStateChanged, type User } from "firebase/auth";
import { getFirebaseAuth } from "./firebase";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

/**
 * Resolve the Firebase user, waiting briefly for auth to initialise
 * if `currentUser` is null on the very first call. The Firebase SDK
 * reads its persisted session asynchronously after page load, so a
 * synchronous `auth.currentUser` check during the first paint reliably
 * returns null even when the user is actually signed in.
 *
 * Without this wait we'd send an unauthenticated request and the API
 * would return 401 — the bug a user reported when opening the app
 * after a long idle period.
 */
const AUTH_WAIT_MS = 3000;

// Once Firebase has fired onAuthStateChanged at least once, `auth.currentUser`
// is authoritative and we read it fresh on every call. We only memoize the
// one-time *initialisation wait*, never the resolved user.
//
// Caching the resolved value instead would poison the auth state: the login
// page renders <Logo>, which calls /me while the user is still signed out,
// resolving the cache to `null`. The post-sign-in redirect is a client-side
// navigation (no page reload), so that stale `null` would survive and every
// subsequent /me would be sent without a token — a permanent 401 loop for
// any user who passes through the login page before their first request.
let firstSettle: Promise<void> | null = null;

function waitForAuth(): Promise<User | null> {
  let auth: ReturnType<typeof getFirebaseAuth>;
  try {
    auth = getFirebaseAuth();
  } catch {
    return Promise.resolve(null);
  }

  // Firebase has already settled once (or restored a session synchronously) —
  // read the live value, don't reuse a stale snapshot.
  if (firstSettle === null && auth.currentUser) {
    return Promise.resolve(auth.currentUser);
  }

  if (firstSettle === null) {
    firstSettle = new Promise<void>((resolve) => {
      let done = false;
      const timeout = setTimeout(() => {
        if (done) return;
        done = true;
        // Auth listener never fired in time — proceed; currentUser is read below.
        resolve();
      }, AUTH_WAIT_MS);

      const unsub = onAuthStateChanged(auth, () => {
        if (done) return;
        done = true;
        clearTimeout(timeout);
        unsub();
        resolve();
      });
    });
  }

  return firstSettle.then(() => auth.currentUser);
}

export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);

  // Only declare a JSON content-type when we're actually sending a body.
  // Fastify's default body parser rejects GET/DELETE requests that carry
  // Content-Type: application/json with no body (FST_ERR_CTP_EMPTY_JSON_BODY).
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  try {
    const user = await waitForAuth();
    if (user) {
      // Force a refresh if the cached token is close to expiry. Passing
      // `false` returns the cached token unless it's within 5 min of
      // expiry, in which case Firebase silently refreshes — which is
      // the behaviour we want for the common case.
      const token = await user.getIdToken();
      headers.set("Authorization", `Bearer ${token}`);
    }
  } catch {
    // Firebase not configured yet — proceed unauthenticated (dev only).
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    // Tag 401s explicitly so callers (or a global error handler) can
    // decide to redirect to /login instead of surfacing a raw error.
    if (res.status === 401) {
      const err = new Error(text || "unauthenticated") as Error & {
        status?: number;
      };
      err.status = 401;
      throw err;
    }
    throw new Error(`API ${res.status}: ${text || res.statusText}`);
  }

  // 204 No Content (deletes, etc.) has no body to parse.
  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined as T;
  }

  return (await res.json()) as T;
}
